/**
 * Monitoring Integration
 * 
 * Utilities for logging errors and events to @butter/monitoring service.
 * 
 * Integrates API client with centralized monitoring for:
 * - Error tracking
 * - Performance metrics
 * - Request metadata
 * 
 * **Note:** Tenant context (clubId, tenantHandle) is automatically synced to Sentry
 * by appRuntime service. No manual setTenantContext() calls needed here.
 * 
 * @module monitoring
 */

import {
  captureException,
  captureMessage,
  addBreadcrumb,
} from '@butter/monitoring';
import type { ErrorContext } from '@butter/monitoring';
import { CLIENT_INFO, ERROR_CODES, IS_PRODUCTION } from '../constants';
import type { RequestOptions } from '../types';

/** The failure's HTTP status: the caller's, else the error's own. 0 is a real value. */
function statusOf(error: unknown, context?: MonitoringContext): number | undefined {
  return context?.status ?? (error as { status?: number } | undefined)?.status;
}

function codeOf(error: unknown): string | undefined {
  const code = (error as { code?: unknown } | undefined)?.code;
  return typeof code === 'string' ? code : undefined;
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** No response reached us: the connection failed or the request timed out. */
function isNetworkShaped(status: number | undefined, code: string | undefined): boolean {
  return status === 0 || code === ERROR_CODES.NETWORK_ERROR || code === ERROR_CODES.TIMEOUT_ERROR;
}

/**
 * Network failures and timeouts while online are worth seeing, but they are usually the
 * member's connection rather than us — a warning. A response we could not use (5xx,
 * parse error) stays an error.
 */
function levelFor(status: number | undefined, code: string | undefined): 'warning' | 'error' {
  return isNetworkShaped(status, code) ? 'warning' : 'error';
}

/**
 * Context for monitoring logs
 */
export interface MonitoringContext {
  /**
   * API endpoint that was called
   */
  endpoint?: string;
  
  /**
   * HTTP method
   */
  method?: string;
  
  /**
   * Tenant handle
   */
  tenantHandle?: string;
  
  /**
   * Club ID (accepts both number and string)
   */
  clubId?: number | string;
  
  /**
   * HTTP status code
   */
  status?: number;
  
  /**
   * Request attempt number (for retries)
   */
  attempt?: number;
  
  /**
   * Additional custom data
   */
  [key: string]: unknown;
}

/**
 * Log error to monitoring service
 * 
 * Automatically includes:
 * - Client identification (app, platform)
 * - Tenant context (tenantHandle, clubId)
 * - Request metadata (endpoint, method, status)
 * 
 * **Error Handling:** This function never throws. If monitoring fails,
 * it logs to console in development mode.
 * 
 * @param error - Error to log
 * @param context - Additional context about the request
 * 
 * @example
 * ```typescript
 * try {
 *   await fetch('/api/bookings');
 * } catch (error) {
 *   logErrorToMonitoring(error, {
 *     endpoint: '/api/bookings',
 *     method: 'GET',
 *     tenantHandle: 'drivingrangecollective',
 *     clubId: 2,
 *   });
 * }
 * ```
 */
export function logErrorToMonitoring(
  error: unknown,
  context?: MonitoringContext
): void {
  try {
    // Convert error to Error instance if needed
    const errorInstance = error instanceof Error 
      ? error 
      : new Error(String(error));

    const status = statusOf(error, context);
    const code = codeOf(error);

    // The device being offline is not something we can fix — a laptop waking from sleep
    // produced these as errors. Keep it as a breadcrumb for whatever is captured next.
    // Only when no response arrived: a 5xx that lands as `onLine` flips is still ours.
    if (isOffline() && isNetworkShaped(status, code)) {
      addBreadcrumb(
        'api',
        `Request failed while offline: ${errorInstance.message}`,
        { endpoint: context?.endpoint, method: context?.method, status, code },
        'warning'
      );
      return;
    }
    
    // Build error context for @butter/monitoring
    const errorContext: ErrorContext = {
      level: levelFor(status, code),
      tags: {
        app: CLIENT_INFO.APP,
        platform: CLIENT_INFO.PLATFORM,
        endpoint: context?.endpoint || 'unknown',
        method: context?.method?.toUpperCase() || 'unknown',
        ...(status !== undefined && { status }),
        ...(code && { code }),
      },
      extra: {
        ...context,
        status,
        code,
        details: (error as { details?: unknown } | undefined)?.details,
      },
    };
    
    // Note: Tenant context (clubId, tenantHandle) is automatically synced to Sentry
    // by appRuntime.syncMonitoringContext(). No manual setTenantContext() needed.
    
    // Capture exception with @butter/monitoring
    captureException(errorInstance, errorContext);
    
    if (!IS_PRODUCTION) {
      console.debug(
        '[ApiClient] 📊 Logged error to monitoring',
        '\nError:', errorInstance.message,
        '\nContext:', errorContext
      );
    }
  } catch (monitoringError) {
    // Don't let monitoring errors break the app
    if (!IS_PRODUCTION) {
      console.error(
        '[ApiClient] ❌ Failed to log error to monitoring',
        '\nOriginal error:', error,
        '\nMonitoring error:', monitoringError
      );
    }
  }
}

/**
 * Log retry attempt to monitoring
 * 
 * Useful for tracking retry patterns and identifying problematic endpoints.
 * 
 * @param context - Request context
 * @param attemptNumber - Current attempt number
 * @param error - Error that triggered the retry
 * 
 * @example
 * ```typescript
 * logRetryToMonitoring({
 *   endpoint: '/api/bookings',
 *   method: 'GET',
 * }, 2, error);
 * ```
 */
export function logRetryToMonitoring(
  context: MonitoringContext,
  attemptNumber: number,
  error: unknown
): void {
  try {
    // Add breadcrumb for retry attempt
    addBreadcrumb(
      'api',
      `Retrying request (attempt ${attemptNumber})`,
      {
        endpoint: context?.endpoint,
        method: context?.method,
        attempt: attemptNumber,
      },
      'warning'
    );
    
    // Build warning message context
    const messageContext: ErrorContext = {
      level: 'warning',
      tags: {
        app: CLIENT_INFO.APP,
        platform: CLIENT_INFO.PLATFORM,
        endpoint: context?.endpoint || 'unknown',
        method: context?.method?.toUpperCase() || 'unknown',
        attempt: attemptNumber,
        is_retry: true,
        ...(context?.status !== undefined && { status: context.status }),
      },
      extra: {
        endpoint: context?.endpoint,
        method: context?.method,
        tenantHandle: context?.tenantHandle,
        clubId: context?.clubId,
        status: context?.status,
        attemptNumber,
        isRetry: true,
        error: error instanceof Error ? error.message : String(error),
        ...context,
      },
    };
    
    // Note: Tenant context is automatically synced by appRuntime
    
    // Capture retry warning with @butter/monitoring
    captureMessage(
      `API request retry attempt ${attemptNumber}`,
      'warning',
      messageContext
    );
    
    if (!IS_PRODUCTION) {
      console.debug(
        '[ApiClient] 📊 Logged retry to monitoring',
        '\nAttempt:', attemptNumber,
        '\nError:', error instanceof Error ? error.message : String(error),
        '\nContext:', messageContext
      );
    }
  } catch (monitoringError) {
    // Don't let monitoring errors break the app
    if (!IS_PRODUCTION) {
      console.error(
        '[ApiClient] ❌ Failed to log retry to monitoring',
        '\nMonitoring error:', monitoringError
      );
    }
  }
}

/**
 * Extract monitoring context from request options
 * 
 * Helper to build MonitoringContext from RequestOptions.
 * 
 * @param options - Request options
 * @param endpoint - API endpoint
 * @param method - HTTP method
 * @returns Monitoring context
 * 
 * @example
 * ```typescript
 * const context = extractMonitoringContext(
 *   { tenantHandle: 'drc', clubId: 2 },
 *   '/api/bookings',
 *   'GET'
 * );
 * ```
 */
export function extractMonitoringContext(
  options: RequestOptions | undefined,
  endpoint: string,
  method: string
): MonitoringContext {
  return {
    endpoint,
    method: method.toUpperCase(),
    tenantHandle: options?.tenantHandle,
    clubId: options?.clubId,
  };
}
